import { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Paper,
  Grid,
  useMediaQuery,
  Fade,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import WidgetsIcon from "@mui/icons-material/Widgets";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import QueueIcon from "@mui/icons-material/Queue";
import MenuIcon from "@mui/icons-material/Menu";
import PaidIcon from "@mui/icons-material/Paid";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import VideoSettingsIcon from "@mui/icons-material/VideoSettings";
import MovieIcon from "@mui/icons-material/Movie";
import Logo from "../images/logo.svg";
import { Link, useLocation } from "react-router-dom";
import theme from "./style/theme";

function CustomLink(props) {
  return (
    <Link
      style={{ textDecoration: "none", color: "inherit" }}
      {...props}
      state={{ heading: true }}
    />
  );
}

const navItems = [
  { label: "Pricing", href: "/pricing" },
  { label: "Blog", href: "/blogs" },
  // { label: "Contact", href: "/get-started" },
];

const useCaseItems = [
  {
    name: "Smart Auto Scaling",
    href: "/use-case/auto-scaling",
    icon: <TipsAndUpdatesIcon />,
  },
  {
    name: "Spot Instance Optimization",
    href: "/use-case/spot-instance",
    icon: <WidgetsIcon />,
  },
  {
    name: "Idle Resource Termination",
    href: "/use-case/termination",
    icon: <IndeterminateCheckBoxIcon />,
  },
  {
    name: "Cost-Effective Scheduling",
    href: "/use-case/scheduling",
    icon: <QueueIcon />,
  },
];

const industryItems = [
  {
    name: "Financial Services",
    href: "/industry/financialServices",
    icon: <PaidIcon sx={{ color: "rgb(99, 99, 99)" }} />,
  },
  {
    name: "Healthcare",
    href: "/industry/healthCare",
    icon: <MedicalInformationIcon sx={{ color: "rgb(99, 99, 99)" }} />,
  },
  {
    name: "SaaS",
    href: "/industry/saas",
    icon: <VideoSettingsIcon sx={{ color: "rgb(99, 99, 99)" }} />,
  },
  {
    name: "Media",
    href: "/industry/media",
    icon: <MovieIcon sx={{ color: "rgb(99, 99, 99)" }} />,
  },
];

const SolutionsMenu = ({ onMouseLeave }) => (
  <Fade in timeout={{ enter: 300, exit: 400 }} unmountOnExit>
    <Paper
      onMouseLeave={onMouseLeave}
      sx={{
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        width: 510,
        p: 4,
        boxShadow: 6,
        borderRadius: 2,
        zIndex: 10,
        direction: "row",
      }}
    >
      <Grid container direction="row">
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              ml: 1,
              fontWeight: "bold",
              color: "text.primary",
            }}
          >
            By Use Case
          </Typography>
          <Grid container spacing={2.8} direction="column">
            {useCaseItems.map((item, idx) => (
              <Grid item key={idx}>
                <Button
                  href={item.href}
                  sx={{
                    justifyContent: "flex-start",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                    px: 2,
                    borderRadius: 2,
                    color: "text.secondary",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "background.paper",
                      transform: "translateX(5px)",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", fontSize: "0.8rem" }}>
                    {item.icon}
                  </ListItemIcon>
                  <Typography
                    sx={{
                      textTransform: "capitalize",
                      fontSize: "0.9rem",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {item.name}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              ml: 1,
              fontWeight: "bold",
              color: "text.primary",
            }}
          >
            By Industry Case
          </Typography>
          <Grid container spacing={2.8} direction="column">
            {industryItems.map((item, idx) => (
              <Grid item key={idx}>
                <Button
                  href={item.href}
                  sx={{
                    justifyContent: "flex-start",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                    px: 1.5,
                    borderRadius: 2,
                    color: "text.secondary",
                    textAlign: "left",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "background.paper",
                      transform: "translateX(5px)",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "inherit", fontSize: "0.8rem" }}>
                    {item.icon}
                  </ListItemIcon>
                  <Typography
                    sx={{
                      textTransform: "capitalize",
                      fontSize: "0.9rem",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {item.name}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  </Fade>
);

const MobileMenu = ({ open, onClose }) => (
  <Drawer anchor="right" open={open} onClose={onClose}>
    <Box
      sx={{ width: 250, p: 2 }}
      role="presentation"
      // onClick={onClose}
      onKeyDown={onClose}
    >
      <IconButton sx={{ mb: 2 }} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <List>
        <ListItem button component={Link} to="/">
          <ListItemText primary="Home" />
        </ListItem>
        <Button sx={{ position: "relative", p: 0 }}>
          <Accordion disableGutters elevation={0}>
            <AccordionSummary
              sx={{ textAlign: "center" }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Typography>Solutions</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: "50%", pr: 2, textAlign: "left", mb: 2 }}>
                <Typography variant="button">By Use Case </Typography>
                <Grid container spacing={1} mt={2} direction="column">
                  {useCaseItems.map((item, idx) => (
                    <Button
                      key={idx}
                      href={item.href}
                      disableElevation
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start", // left align
                        alignItems: "center",
                        gap: 1,
                        py: 0.25, // less vertical padding
                        // minWidth: "unset", // remove default 64px
                        minWidth: 300,
                        borderRadius: 2,
                        color: "rgb(92, 91, 91)",
                        textAlign: "left",
                        backgroundColor: "transparent",
                        textTransform: "none", // prevent uppercase
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "rgb(8, 8, 8)",
                          backgroundColor: "rgba(3, 58, 120, 0.05)",
                          transform: "translateX(5px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {item.icon}
                      <Typography
                        variant="body2" // smaller, no extra spacing
                        sx={{ lineHeight: 1.4 }}
                      >
                        {item.name}
                      </Typography>
                    </Button>
                  ))}
                </Grid>
              </Box>
              <Box sx={{ width: "100%", pr: 2, textAlign: "left" }}>
                <Typography variant="button">By Industry Case</Typography>
                <Grid container spacing={1} mt={2} direction="column">
                  {industryItems.map((item, idx) => (
                    <Button
                      key={idx}
                      href={item.href}
                      disableElevation
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start", // left align
                        alignItems: "center",
                        gap: 1,
                        py: 0.25, // less vertical padding
                        // px: 1, // less horizontal padding
                        minWidth: "unset", // remove default 64px
                        borderRadius: 2,
                        color: "rgb(92, 91, 91)",
                        textAlign: "left",
                        backgroundColor: "transparent",
                        textTransform: "none", // prevent uppercase
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "rgb(8, 8, 8)",
                          backgroundColor: "rgba(3, 58, 120, 0.05)",
                          transform: "translateX(5px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      {item.icon}
                      <Typography
                        variant="body2" // smaller, no extra spacing
                        sx={{ lineHeight: 1.4 }}
                      >
                        {item.name}
                      </Typography>
                    </Button>
                  ))}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Button>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.label}
            component={CustomLink}
            to={item.href}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 1, mt: 2 }}>
        <Button href="/get-started" variant="contained" fullWidth>
          Book a Demo
        </Button>
      </Box>
    </Box>
  </Drawer>
);

export default function TopNavbar() {
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      const section = document.getElementById(hash);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ borderBottom: "1px solid #eee" }}
      >
        <Toolbar
          sx={{ justifyContent: "space-between", mx: { lg: 16, xs: 2 }, py: 2 }}
        >
          <Typography
            variant="h5"
            component={Link}
            to="/"
            sx={{ textDecoration: "none" }}
          >
            <img src={Logo} height="90px" alt="logo" />
          </Typography>

          {isMobile ? (
            <IconButton onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Button component={Link} to="/" color="inherit">
                Home
              </Button>
              <Box
                onMouseEnter={() => setSolutionsOpen(true)}
                onMouseLeave={() => setSolutionsOpen(false)}
                sx={{ position: "relative" }}
              >
                <Button component={Link} to="/solutions" color="inherit">
                  Solutions
                </Button>
                {solutionsOpen && (
                  <SolutionsMenu onMouseLeave={() => setSolutionsOpen(false)} />
                )}
              </Box>
              {navItems.map((item) => (
                <Button
                  key={item.label}
                  component={CustomLink}
                  to={item.href}
                  color="inherit"
                >
                  {item.label}
                </Button>
              ))}
              <Button href="/get-started" variant="contained">
                Book a Demo
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </ThemeProvider>
  );
}
